/**
 * API URL parsing utilities for handling legacy and modern URL formats.
 *
 * The backend API may return URLs in two formats:
 * - Legacy: "GET domain/api/path?params" (with HTTP method prefix, no protocol)
 * - Modern: "https://domain/api/path?params" (direct URL with protocol)
 *
 * These utilities normalize both formats for consistent usage.
 */

/**
 * Checks if URL is in legacy format (prefixed with HTTP method like "GET ")
 */
export const isLegacyApiUrl = (url: string): boolean =>
  url?.split(' ').length > 1;

/**
 * Extracts the raw URL from legacy format, removing the HTTP method prefix.
 * For legacy format "GET domain/..." returns "domain/..."
 * For modern format returns as-is.
 */
export const extractRawUrl = (url: string): string => {
  if (!url) return '';
  if (isLegacyApiUrl(url)) {
    // Legacy format: "GET domain/api/..." -> "domain/api/..."
    return url.substring(url.indexOf(' ') + 1);
  }
  return url;
};

/**
 * Normalizes API URL to HTTPS format.
 * Handles both legacy ("GET domain/...") and modern ("https://...") formats.
 */
export const normalizeApiUrl = (url: string): string => {
  if (!url) return '';
  if (isLegacyApiUrl(url)) {
    // Legacy format: "GET domain/api/..." -> "https://domain/api/..."
    return 'https://' + extractRawUrl(url);
  }
  return url;
};

/**
 * Extracts host (domain) from API URL, stripping protocol if present.
 * Handles both legacy and modern formats.
 *
 * Examples:
 * - "GET domain/api/path" -> "domain"
 * - "https://domain/api/path" -> "domain"
 */
export const extractApiHost = (url: string): string => {
  if (!url) return '';
  const rawUrl = extractRawUrl(url);

  // Check if URL has protocol
  if (rawUrl.includes('://')) {
    const withoutProtocol = rawUrl.split('://')[1];
    if (!withoutProtocol) return '';
    return withoutProtocol.split('/')[0];
  }

  // Legacy format without protocol: "domain/api/path"
  return rawUrl.split('/')[0];
};

/**
 * Constructs WebSocket URL from API URL.
 * Handles both legacy and modern formats.
 *
 * @param url - The API URL (legacy or modern format)
 * @param wsPath - Optional path to append (e.g., '/stream')
 */
export const toWebSocketUrl = (url: string, wsPath?: string): string => {
  if (!url) return '';
  const rawUrl = extractRawUrl(url);
  const urlParts = rawUrl.split('?');
  const pathPart = urlParts[0];

  // Extract host and path, handling both formats
  let hostAndPath: string;
  if (pathPart.includes('://')) {
    // Modern format: "https://domain/api/path"
    hostAndPath = pathPart.split('://')[1];
  } else {
    // Legacy format: "domain/api/path"
    hostAndPath = pathPart;
  }

  if (!hostAndPath) return '';

  const basePath = wsPath ? `${hostAndPath}${wsPath}` : hostAndPath;
  const queryString = urlParts[1] ? `?${urlParts[1]}` : '';

  return `wss://${basePath}${queryString}`;
};
